import type { IApiListResponse, IBaseModel } from '../types';

export async function toArray(
	asyncIterator: AsyncGenerator<IApiListResponse<IBaseModel>, void, unknown>
) {
	const arr: IBaseModel[] = [];

	while (true) {
		// eslint-disable-next-line no-await-in-loop
		const { done, value } = await asyncIterator.next();
		if (done) break;
		arr.push(...value.results);
	}

	return arr;
}

/**
 * Generic utility to fetch all pages of a codegen API.
 * Warning: This function will not stop until it has fetched all pages of the list
 * so do NOT run it unless it is necessary.
 *
 * @param fetchFn - The fetch function to use
 * @param variables - The variables to use
 * @param rest - The rest of the arguments to use
 * @returns All the results from all the pages
 */
export async function codegenApiFetchAllPages<T, V, R>(
	fetchFn: (
		variables: V & { queryParams?: { page?: number } },
		...rest: R[]
	) => Promise<{
		results: T[];
		total_pages: number;
		count: number;
	}>,
	variables: V & { queryParams?: { page?: number } },
	...rest: R[]
): Promise<{
	results: T[];
	total_pages: number;
	count: number;
}> {
	const allResults: T[] = [];
	const concreteVariables = variables;

	if (!concreteVariables.queryParams) {
		concreteVariables.queryParams = { page: 1 };
	} else if (!concreteVariables.queryParams.page) {
		concreteVariables.queryParams.page = 1;
	}

	// Fetch first page to get total_pages
	const firstResponse = await fetchFn(concreteVariables, ...rest);

	allResults.push(...firstResponse.results);

	const promises = [];

	// Fetch remaining pages if there are more
	if (firstResponse.total_pages > 1) {
		for (let page = 2; page <= firstResponse.total_pages; page += 1) {
			promises.push(
				fetchFn(
					{
						...concreteVariables,
						queryParams: { ...concreteVariables.queryParams, page },
					},
					...rest
				)
			);
		}
		const pageResponses = await Promise.all(promises);
		for (const pageResponse of pageResponses) {
			allResults.push(...pageResponse.results);
		}
	}

	return {
		total_pages: firstResponse.total_pages,
		results: allResults,
		count: allResults.length,
	};
}
